/* -----------------------Globals-------------------- */
import { useCallback, useEffect, useState } from "react";

/* -----------------------UI-------------------- */
import Container from "@memorang/ui/src/components/Container";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
/* -----------------------Hooks-------------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { Redirect, useLocalSearchParams } from "expo-router";

/* -----------------------Helpers-------------------- */
import { events } from "@constants/tracking";
import { createRelativeURL } from "@helpers/url";
import { composeMessage } from "@memorang/helpers/src/email";
import { trackCustomEvent } from "analytics";

import { GREETING_MESSAGE, useRedeemCopilot } from "@features/redeem-copilot";

/* -----------------------Session-------------------- */
import { getSupportDetails, handleAccessCodeResult } from "../helpers";

import {
	useRcContactSupport,
	useRcGetPurchases,
	useRcMatchEmail,
	useRcRedeemCode,
	useRcResendCodes,
} from "@features/redeem-copilot/hooks";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import { validateCode } from "@helpers/string-utils";
import useClerkUser from "@hooks/useClerkUser";
import { applyAccessCode } from "api/commerce/access-code-service";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
/* -----------------Child components--------------- */
import CollectUserDetailsDialogContainer from "./CollectUserDetailsDialogContainer";
import PostRedeemOlxDialog from "./PostRedeemOlxDialog";
import RedeemCodeView from "./RedeemCodeView";
import SessionHelpDialog from "./SessionHelpDialog";

type Props = {
	handleNext: (result: ApplyAccessCodeResponse) => void;
};

const RedeemCodeContainer = ({ handleNext }: Props) => {
	const supportEmail = useAppStore((store) => store.supportEmail);
	const company = useAppStore((store) => store.app.logo.company);
	const appName = useAppStore((store) => store.app.name);
	const appId = useAppStore((store) => store.app.id);

	const [isCodeRedeemedBySameUser, setIsCodeRedeemedBySameUser] =
		useState(false);

	const [helpDialogVisible, setHelpDialogVisible] = useState(false);

	const [showCollectUserDialog, setShowCollectUserDialog] = useState(false);

	const { setPartialChatState, pushContextMessage } = useRedeemCopilot();

	const toggleShowCollectUserDialog = () => {
		setShowCollectUserDialog((prev) => !prev);
	};

	const [olxDates, setOlxDates] = useState<{
		beginStartDate: string;
		endStartDate: string;
		tempAccessCodeResponse?: ApplyAccessCodeResponse;
	} | null>(null);

	const params = useLocalSearchParams<{
		redeemCode: string;
		showOnboarding: string;
	}>();

	const pathCode = params ? params.redeemCode : "";
	const showOnboarding = params ? params.showOnboarding : "";

	const theme = useAppTheme();

	const [code, setCode] = useState(pathCode);

	const {
		currentBlock: sessionItems,
		numSessionItems,
		numTotalItems,
	} = useCurrentSession();
	const examName = useSessionStore((state) => state.examName);
	const totalTime = useSessionStore((state) => state.totalTime);
	const numericId = useSessionStore((state) => state.numericId);

	useEffect(() => {
		setCode(pathCode);
	}, [pathCode]);

	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);

	const [loading, setLoading] = useState(false);

	const { loading: authLoading } = useAuthContext();

	const {
		userLogicalId: viewerId,
		username,
		email,
		isSignedIn,
	} = useClerkUser();

	const { handleSwitchExamContext } = useExamContext();

	const showError = errorMessage != null;

	// biome-ignore lint/correctness/useExhaustiveDependencies: Valid use case
	const handleApplyCode = useCallback(
		async ({ code }: { code: string }) => {
			const validationError = validateCode(code);

			if (validationError) {
				trackCustomEvent({
					eventName: events.promoCodeError,
					code,
					error: validationError,
				});

				return { success: false, error: validationError };
			}

			trackCustomEvent({
				eventName: events.promoCodeEntered,
				code,
			});

			try {
				const result = await applyAccessCode({ accessCode: code });
				const { shouldHandleNext } = await handleAccessCodeResult(
					result,
					handleSwitchExamContext,
					setOlxDates,
				);

				return {
					success: true,
					error: "",
					result,
					shouldHandleNext,
				};
			} catch (e) {
				let error = "An unexpected error occurred.";

				if (e instanceof Error) {
					error = e.message;
				}

				if (
					e &&
					typeof e === "object" &&
					"response" in e &&
					e.response &&
					typeof e.response === "object" &&
					"data" in e.response &&
					e.response.data &&
					typeof e.response.data === "object" &&
					"message" in e.response.data &&
					typeof e.response.data.message === "string"
				) {
					error = e.response.data.message;
				}

				trackCustomEvent({
					eventName: events.promoCodeError,
					code,
					error,
				});

				return {
					success: false,
					error,
					shouldHandleNext: false,
				};
			}
		},
		[email, appId],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Valid use case
	const handleSubmit = useCallback(async () => {
		setLoading(true);
		const { success, error, result, shouldHandleNext } = await handleApplyCode({
			code,
		});

		setLoading(false);

		if (result?.isSameEmail) {
			setIsCodeRedeemedBySameUser(true);
			setErrorMessage(result.message);
			return;
		}
		if (success) {
			if (shouldHandleNext && result) {
				handleNext(result);
			}
			return;
		}

		setErrorMessage(error);
	}, [code, handleApplyCode]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Valid use case
	const handleAskAssistant = useCallback(() => {
		const errMsg =
			errorMessage || "User does not have/could not find an Access Code.";

		pushContextMessage(
			JSON.stringify({
				type: "error",
				message: errMsg,
				instructions: GREETING_MESSAGE,
			}),
		);

		setPartialChatState({
			chatOpen: true,
		});
	}, [username, email, errorMessage]);

	const handlePressContactSupport = () => {
		const { subject, body } = getSupportDetails(appName, username, email || "");
		composeMessage(subject, body, supportEmail);
	};

	useEffect(() => {
		if (pathCode && isSignedIn && !authLoading && viewerId) {
			handleSubmit();
		}
	}, [authLoading, handleSubmit, isSignedIn, pathCode, viewerId]);

	const handleChangeText = (text: string) => {
		if (isCodeRedeemedBySameUser) {
			setIsCodeRedeemedBySameUser(false);
		}

		if (showError) {
			setErrorMessage(undefined);
		}
		setCode(text);
	};

	const disableFab = !code || code.length < 6 || loading;

	const getCompanyLogo = () => {
		if (company) {
			const { light, dark } = company;
			return theme.dark ? dark : light;
		}
		return null;
	};

	const logo = getCompanyLogo();

	useRcRedeemCode({
		handler: handleApplyCode,
	});
	useRcResendCodes();
	useRcGetPurchases();
	useRcMatchEmail();
	useRcContactSupport({
		supportDetails: {
			supportEmail,
			appName,
			...getSupportDetails(appName, username, email || ""),
		},
		handler: handlePressContactSupport,
	});

	if (!isSignedIn || authLoading) {
		const newUrl = createRelativeURL("/(auth)/login", params);

		return <Redirect href={newUrl} />;
	}

	return (
		// biome-ignore lint/a11y/useSemanticElements: <explanation>
		<Container maxWidth="xs" role="main">
			<RedeemCodeView
				title={appName}
				subtitle="Enter your complete access code including the dashes"
				logo={logo}
				defaultCode={pathCode}
				handleChangeText={handleChangeText}
				handleSubmit={handleSubmit}
				disableFab={disableFab}
				isCodeRedeemedBySameUser={isCodeRedeemedBySameUser}
				loading={loading}
				handleAskAssistant={handleAskAssistant}
				email={email || ""}
				errorMessage={errorMessage}
				showError={showError}
				showOnboarding={showOnboarding}
				handlePressContactSupport={handlePressContactSupport}
			/>
			<SessionHelpDialog
				helpDialogVisible={helpDialogVisible}
				sessionItems={sessionItems}
				examName={examName}
				totalTime={totalTime}
				numericId={numericId}
				setHelpDialogVisible={setHelpDialogVisible}
				numSections={numSessionItems}
				numTotalQuestions={numTotalItems}
			/>
			<PostRedeemOlxDialog
				olxDates={olxDates}
				setOlxDates={setOlxDates}
				handleNext={handleNext}
				toggleShowCollectUserDialog={toggleShowCollectUserDialog}
			/>
			<CollectUserDetailsDialogContainer
				showCollectUserDialog={showCollectUserDialog}
				toggleShowCollectUserDialog={toggleShowCollectUserDialog}
				setHelpDialogVisible={setHelpDialogVisible}
			/>
		</Container>
	);
};

export default RedeemCodeContainer;
